import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMutation, useQueryClient } from 'react-query'
import PropTypes from 'prop-types'
import ReactTooltip from 'react-tooltip'
import styled from 'styled-components'
import colors from '../../../configuration/colors'
import { hideModal } from '../../../actions/modal'
import Modal from '../../mini/Modal'
import TextInputComponent from './InputComponents/TextInputComponent'
import Button from '../../mini/Buttons/Button'
import CancelButton from '../../mini/Buttons/CancelButton'
import H2 from '../../typography/headers/H2'
import MarginRight15 from '../../typography/margins/MarginRight15'
import { editMentorActivityCourse } from '../../mycourses/API'
import withApiData from '../../HOC/withApiData'
import StaticInputValue from './InputComponents/StaticInputValue'
import EditCourseIcon from '../../../static/images/mycourses/icon_edit_course.svg'
import { cleanHexCode } from '../../mycourses/util'
import {PopoverPicker} from '../../colorpicker/PopoverPicker'

const ModalContainer = styled.div`
  width: 660px;
  margin: 100px auto;
  border-radius: 10px;
  box-shadow: 0 1px 9px 0 rgba(0, 0, 0, 0.08);
  background-color: ${colors.smoke};
`

const HeaderContainer = styled.div`
  height: 80px;
  width: auto;
  border-radius: 10px 10px 0 0;
  background-color: ${colors.white};
  border-bottom: 1px solid ${colors.rock};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 30px;
`

const MainContainer = styled.div`
  padding: 10px 30px 30px;
`

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const ColorPicker = styled.input`
  margin-left: 30px;
  margin-top: 7.2%;
`

const EditCourseModal = ({
  apiData,
  activityCourse,
  academicYears,
  selectedAcademicYearId
}) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const [isMutating, setIsMutating] = useState(false)

  const academicYearName = academicYears.find(
    ay => ay.id === activityCourse.academic_year_id
  ).name

  const gradeLevels = useSelector(state => state.gradelevels.items)
  const gradeLevelName = gradeLevels.find(
    gl => gl.id === activityCourse.grade_level_id
  ).name

  const frameworkSubjects = useSelector(state => state.frameworksubjects.items)
  const frameworkSubjectName = frameworkSubjects.find(
    fs => fs.id === activityCourse.framework_subject_id
  ).name

  const [courseName, setCourseName] = useState(activityCourse.name)
  const handleCourseName = e => {
    setCourseName(e.target.value)
  }

  const [abbreviation, setAbbreviation] = useState(activityCourse.abbreviation)
  const handleAbbreviation = e => {
    setAbbreviation(e.target.value)
  }

  const [courseColor, setCourseColor] = useState(
    cleanHexCode(activityCourse.color)
  )
  const handleCourseColor = e => {
    setCourseColor(e.target.value)
  }

  const isValidColor = str => {
    return str.match(/^#[a-f0-9]{6}$/i) !== null
  }

  const couldSaveData =
    courseName.length > 0 &&
    abbreviation.length > 0 &&
    abbreviation.length <= 3 &&
    isValidColor(courseColor === '' ? '#4E6C83' : courseColor)

  const handleCloseModal = () => {
    dispatch(hideModal())
  }

  const mutation = useMutation(
    ({ courseName, abbreviation, courseColor, selectedAcademicYearId }) =>
      editMentorActivityCourse(
        apiData,
        courseName,
        abbreviation,
        courseColor,
        activityCourse,
        selectedAcademicYearId
      )
  )

  const handleSaveEditedCourse = () => {
    if (couldSaveData) {
      setIsMutating(true)
      mutation.mutate(
        {
          courseName,
          abbreviation,
          courseColor: courseColor === '' ? '#4E6C83' : courseColor,
          selectedAcademicYearId
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(
              'mentor-master-courses-' + selectedAcademicYearId
            )
            setTimeout(() => {
              setIsMutating(false)
              handleCloseModal()
            }, 300)
          }
        }
      )
    }
  }

  return (
    <Modal
      width={1400}
      percentHeight={100}
      background={'rgba(36,54,70,0.48)'}
      lockBodyScroll={false}
      opacity={'.9'}
      allowInnerScroll
      allowCloseOnExternalClick={false}
    >
      <ModalContainer>
        <HeaderContainer>
          <H2>
            <span style={{ marginRight: '12px' }}>
              <img src={EditCourseIcon} />
            </span>
            Edit Course
          </H2>
          <FlexContainer>
            <MarginRight15>
              <CancelButton
                handleClick={handleCloseModal}
                title="Cancel"
                width="104px"
              />
            </MarginRight15>
            <div
              data-for="editCourseTooltip"
              data-tip="Please enter all required fields prior to creating this course."
            >
              <Button
                width="104px"
                title="Save"
                handleClick={handleSaveEditedCourse}
                buttonColor={colors.oeYellow}
                borderColor={colors.oeYellow}
                hoverBackground={colors.charcoal4}
                hoverColor={colors.white}
                isMutating={isMutating}
                couldSaveData={couldSaveData}
                shouldShowTooltipAndDisableButton={true}
              />
              {!couldSaveData && (
                <ReactTooltip
                  id="editCourseTooltip"
                  place="bottom"
                  effect="solid"
                  className="activityCourseTooltip"
                />
              )}
            </div>
          </FlexContainer>
        </HeaderContainer>
        <MainContainer>
          <StaticInputValue
            placeholder="Academic Year"
            textValue={academicYearName}
          />
          <StaticInputValue placeholder="Phase" textValue={gradeLevelName} />
          <StaticInputValue
            placeholder="Framework Subject"
            textValue={frameworkSubjectName}
          />
          <TextInputComponent
            title="Course Name"
            hintText="Example: Spanish 2, Art 1, Psychology"
            handleEnteredTextValue={handleCourseName}
            value={courseName}
            shouldDisplayAsteriskSymbol={true}
          />
          <TextInputComponent
            title="Abbreviation"
            hintText="Must be no more than 3 letters. Example: Sp1, Psy, Ar1"
            handleEnteredTextValue={handleAbbreviation}
            value={abbreviation}
            shouldDisplayAsteriskSymbol={true}
          />
          <FlexContainer>
            <TextInputComponent
              title="Course Color Hex Code"
              placeholder="Course Color Hex Code"
              hintText="If left empty, #4e6c84 will be applied."
              // cannot choose a hex code to respect color accessibility.
              // handleEnteredTextValue={handleCourseColor}
              value={courseColor}
              shouldDisplayAsteriskSymbol={false}
            />
            <PopoverPicker color={courseColor === '' ? '#4e6c84' : cleanHexCode(courseColor)} onChange={setCourseColor} />
          </FlexContainer>
          {/* <FlexContainer> */}
          {/*   <TextInputComponent */}
          {/*     title="Course Color Hex Code" */}
          {/*     hintText="If left empty,  #4E6C84 will be applied." */}
          {/*     handleEnteredTextValue={handleCourseColor} */}
          {/*     value={courseColor} */}
          {/*     shouldDisplayAsteriskSymbol={false} */}
          {/*   /> */}
          {/*   <ColorPicker */}
          {/*     type="color" */}
          {/*     id="head" */}
          {/*     name="head" */}
          {/*     value={courseColor === '' ? '#4E6C83' : courseColor} */}
          {/*     onChange={handleCourseColor} */}
          {/*   /> */}
          {/* </FlexContainer> */}
        </MainContainer>
      </ModalContainer>
    </Modal>
  )
}

EditCourseModal.propTypes = {
  apiData: PropTypes.object.isRequired,
  activityCourse: PropTypes.object.isRequired,
  academicYears: PropTypes.array.isRequired,
  selectedAcademicYearId: PropTypes.number.isRequired
}

export default withApiData(EditCourseModal)



// WEBPACK FOOTER //
// ./src/components/modal/CreateOrEditCourseModal/EditCourseModal.js